import { isInteractionNoteSource } from "../FileViewer/services/interactionNoteSource";
import { InteractionChoices } from "../FileViewer/InteractionChoices";
import { useViewerInteraction } from "../FileViewer/services/useInteractionByBlobId";
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Tooltip } from "@mui/material";
import {
  highlightTextNodes,
  removeNodeHighlights,
} from "../common/utils/addOnServices/highlightTextNodes";
import { FPNote } from "./FPNoteInterface";
import { updateInteraction } from "../FpPages/RMS/Dashboard/api";
import { createEntitySlug } from "../FpPages/RMS/Dashboard/utils/slugUtils";
import {
  viewerNoteToggleLabels,
  combinedManualSectionLabel,
} from "../FpPages/RMS/Pages/noteToggleRouting";
import {
  FileText,
  User,
  Calendar,
  Tag,
  Users,
  Link,
  X,
  Sparkles,
  Layers,
  Paperclip,
  Plus,
} from "lucide-react";
import openFile from "../FpAppV4/services/openFile";
import EntityIcon from "../FpPages/RMS/Dashboard/Components/EntityIcon";
import TranscriptModal from "../FpPages/RMS/Components/TranscriptModal";
import { FileSelectionModal as V2FileSelectionModal } from "../common/FileSelectionModal";
import { formatTemporalWithTime, type Temporal } from "@/utils/temporal";

const FPNotePageViewer = ({
  response,
  blobId,
  interactionId,
}: {
  response?: any;
  blobId?: string;
  /** Explicit row identity, including when several interactions share blobId. */
  interactionId?: string;
}) => {
  const [responseData, setResponseData] = useState<FPNote | null>(null);
  const [content, setContent] = useState<string>("");
  const {
    data: resolvedInteraction,
    isLoading: isLoadingInteraction,
    error,
    refetch,
  } = useViewerInteraction(blobId, interactionId);
  const interaction =
    resolvedInteraction &&
    (interactionId ||
      (blobId && isInteractionNoteSource(resolvedInteraction, blobId)))
      ? resolvedInteraction
      : undefined;
  // A bare attachment is authorized by the blob loader. Its containing
  // meeting may be unavailable without making the file's own bytes unreadable.
  const interactionError = interactionId ? (error?.message ?? null) : null;
  const selection = `${blobId ?? ""}:${interactionId ?? ""}`;
  const selectionRef = useRef(selection);
  selectionRef.current = selection;
  const [notesContentMode, setNotesContentMode] = useState<
    "notes" | "enhanced" | "all"
  >("notes");
  const [showTranscriptModal, setShowTranscriptModal] = useState(false);
  const [showAllParticipants, setShowAllParticipants] = useState(false);
  const [showAllMentions, setShowAllMentions] = useState(false);
  const [localDocumentIds, setLocalDocumentIds] = useState<string[]>([]);
  const [showDocumentSelectionModal, setShowDocumentSelectionModal] =
    useState(false);
  const participantsRef = useRef<HTMLDivElement>(null);
  const mentionsRef = useRef<HTMLDivElement>(null);
  const [participantVisibleCount, setParticipantVisibleCount] = useState<
    number | null
  >(null);
  const [mentionVisibleCount, setMentionVisibleCount] = useState<number | null>(
    null
  );
  const fileViewerData = useSelector((state: any) => state.fileViewerData);
  const openEntityPreview = (entity: {
    id: string;
    kind: string;
    name: string;
  }) => {
    window.dispatchEvent(
      new CustomEvent("openEntityPreview", {
        detail: {
          entityKind: entity.kind,
          entityId: entity.id,
          // Carried so a listener that navigates instead of previewing can
          // build the slugged entity URL rather than a bare-id one (#9746).
          entityName: entity.name,
        },
      })
    );
  };

  const getEntityUrl = (entity: { id: string; kind: string; name: string }) => {
    const slug = createEntitySlug({ name: entity.name, id: entity.id });
    return `/rms/${entity.kind}/${slug}`;
  };

  // Helper function to check if content is effectively empty
  const isContentEmpty = (html: string | undefined): boolean => {
    if (!html || html.trim() === "") return true;
    const textContent = html.replace(/<[^>]*>/g, "").trim();
    return textContent === "";
  };

  const applyNoteData = (data: FPNote) => {
    setResponseData(data);
    setContent(data.content_html ?? "");

    // Determine default notes content mode based on available content
    const hasNotes = !isContentEmpty(data.content_html);
    const hasEnhanced = !isContentEmpty(data.enhanced_notes);

    if (hasNotes && hasEnhanced) {
      setNotesContentMode("all");
    } else if (!hasNotes && hasEnhanced) {
      setNotesContentMode("enhanced");
    } else {
      setNotesContentMode("notes");
    }
  };

  useEffect(() => {
    let current = true;
    setContent("");
    setResponseData(null);
    setLocalDocumentIds(interaction?.document_ids ?? []);
    if (interaction) {
      // The chosen row owns live content, including the canonical bare-blob
      // choice. Blob JSON is an ingestion snapshot and can predate edits.
      applyNoteData({
        content_html: interaction.notes ?? "",
        enhanced_notes: interaction.enhanced_notes ?? undefined,
      });
    } else if (!interactionId && !isLoadingInteraction && response) {
      response.json().then((data: FPNote) => {
        if (current) applyNoteData(data);
      });
    }
    return () => {
      current = false;
    };
  }, [response, interaction, interactionId, isLoadingInteraction, error]);

  useEffect(() => {
    if (content) {
      // Cap fuzzy highlighting at one block: a "line" here is a whole
      // paragraph, and the matcher's extend-to-query-length behaviour tints
      // blocks all over the note for a paragraph-length claim (#11496).
      highlightTextNodes("#fp-note-content", {
        ...fileViewerData,
        maxHighlightLines: 1,
      });
    }
  }, [fileViewerData, content]);

  useLayoutEffect(() => {
    if (!participantsRef.current || showAllParticipants) return;
    const container = participantsRef.current;
    const containerRect = container.getBoundingClientRect();
    const children = Array.from(container.children) as HTMLElement[];
    const visible = children.filter(
      (child) => child.getBoundingClientRect().top < containerRect.bottom
    ).length;
    const total = interaction?.primary_participants?.length ?? 0;
    // Reserve space for the "+N more" button by showing one fewer chip
    setParticipantVisibleCount(
      visible < total ? Math.max(visible - 1, 1) : visible
    );
  }, [interaction?.primary_participants, showAllParticipants]);

  useLayoutEffect(() => {
    if (!mentionsRef.current || showAllMentions) return;
    const container = mentionsRef.current;
    const containerRect = container.getBoundingClientRect();
    const children = Array.from(container.children) as HTMLElement[];
    const visible = children.filter(
      (child) => child.getBoundingClientRect().top < containerRect.bottom
    ).length;
    const total = interaction?.mentioned_entities?.length ?? 0;
    setMentionVisibleCount(
      visible < total ? Math.max(visible - 1, 1) : visible
    );
  }, [interaction?.mentioned_entities, showAllMentions]);

  // Same rule as every other surface showing an occurrence (#11055): its own
  // granularity decides whether there is a time to print, and its own zone is
  // the one it prints in.
  const formatDate = (occurrence: Temporal) =>
    formatTemporalWithTime(occurrence, "weekdayMedium");

  // Check if enhanced notes exist
  const hasEnhancedNotes =
    !!responseData?.enhanced_notes &&
    !isContentEmpty(responseData.enhanced_notes);

  // A group note's notes half is the shared team document, and this surface
  // has no personal editor to route "My Notes" to, so the toggle and the
  // stacked view name it for what it is (#11763). Read off the interaction
  // fetch, which carries `is_group_note` since #11759.
  const isGroupNote = interaction?.is_group_note === true;
  const toggleLabels = viewerNoteToggleLabels(isGroupNote);
  const manualSectionBannerLabel = combinedManualSectionLabel(isGroupNote);

  // Check if transcript exists (from blob data or interaction data)
  const hasTranscript = !!(responseData?.transcript || interaction?.transcript);
  const transcriptContent = responseData?.transcript || interaction?.transcript;

  const handleDocumentIdsChange = async (document_ids: string[]) => {
    if (!interaction?.id) return;
    try {
      await updateInteraction(interaction.id, { document_ids });
      if (selectionRef.current !== selection) return;
      await refetch();
    } catch (error) {
      console.error("Failed to update document associations:", error);
    }
  };

  const handleDocumentsSelected = async (fileIds: string[]) => {
    const mergedDocumentIds = [...new Set([...localDocumentIds, ...fileIds])];
    await handleDocumentIdsChange(mergedDocumentIds);
  };

  // Get the content to display based on mode
  const getDisplayContent = () => {
    if (notesContentMode === "enhanced" && responseData?.enhanced_notes) {
      return responseData.enhanced_notes;
    }
    return content;
  };

  // Notes content mode toggle component
  const NotesContentToggle = () => {
    if (!hasEnhancedNotes) return null;

    return (
      <div className="flex items-center bg-gray-100 rounded-md p-0.5">
        <Tooltip title={toggleLabels.notes}>
          <button
            onClick={() => setNotesContentMode("notes")}
            className={`p-1.5 rounded transition-all ${
              notesContentMode === "notes"
                ? "bg-white text-gray-800 shadow-sm"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            <FileText className="w-4 h-4" />
          </button>
        </Tooltip>
        <Tooltip title={toggleLabels.enhanced}>
          <button
            onClick={() => setNotesContentMode("enhanced")}
            className={`p-1.5 rounded transition-all ${
              notesContentMode === "enhanced"
                ? "bg-white text-blue-500 shadow-sm"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            <Sparkles className="w-4 h-4" />
          </button>
        </Tooltip>
        <Tooltip title={toggleLabels.all}>
          <button
            onClick={() => setNotesContentMode("all")}
            className={`p-1.5 rounded transition-all ${
              notesContentMode === "all"
                ? "bg-white text-gray-800 shadow-sm"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            <Layers className="w-4 h-4" />
          </button>
        </Tooltip>
      </div>
    );
  };

  return (
    <div className="h-full w-full overflow-y-auto">
      {blobId && !interactionId && (
        <InteractionChoices blobId={interaction?.document_id || blobId} />
      )}
      {/* Metadata section */}
      <div className="w-full bg-white text-[13px]">
        <div className="px-4 pt-3 pb-4 space-y-3">
          {isLoadingInteraction ? (
            <>
              <div className="grid grid-cols-3 gap-4">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="space-y-2">
                    <div className="h-4 w-16 bg-gray-200 rounded animate-pulse" />
                    <div className="h-4 w-24 bg-gray-100 rounded animate-pulse" />
                  </div>
                ))}
              </div>
              <div className="space-y-2">
                <div className="h-4 w-20 bg-gray-200 rounded animate-pulse" />
                <div className="flex flex-wrap gap-2">
                  {[180, 200, 160, 220, 140, 170].map((w, i) => (
                    <div
                      key={i}
                      className={`h-6 rounded-full animate-pulse ${i < 4 ? "bg-blue-100" : "bg-blue-50"}`}
                      style={{ width: `${w}px` }}
                    />
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <div className="h-4 w-16 bg-gray-200 rounded animate-pulse" />
                <div className="flex flex-wrap gap-2">
                  {[120, 150].map((w, i) => (
                    <div
                      key={i}
                      className="h-6 bg-blue-50 rounded-full animate-pulse"
                      style={{ width: `${w}px` }}
                    />
                  ))}
                </div>
              </div>
            </>
          ) : interactionError ? (
            <div className="text-center text-red-600 text-xs">
              {interactionError}
            </div>
          ) : interaction ? (
            <>
              {interaction.superseded_from_blob_id ? (
                <div
                  role="status"
                  className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900"
                >
                  Showing the latest version of these notes. The cited copy was
                  replaced when the meeting notes were updated.
                </div>
              ) : null}
              {/* Basic Info Row */}
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-1">
                  <div className="flex items-center gap-2 text-gray-600">
                    <User className="w-[14px] h-[14px]" />
                    <span className="font-medium">Author</span>
                  </div>
                  {/* Several authors when a group note had several recorders (#9968). */}
                  <div className="text-xs">{interaction.author.join(", ")}</div>
                </div>
                <div className="space-y-1">
                  <div className="flex items-center gap-2 text-gray-600">
                    <Calendar className="w-[14px] h-[14px]" />
                    <span className="font-medium">Date</span>
                  </div>
                  <div className="text-xs">
                    {formatDate(interaction.occurrence)}
                  </div>
                </div>
                <div className="space-y-1">
                  <div className="flex items-center gap-2 text-gray-600">
                    <Tag className="w-[14px] h-[14px]" />
                    <span className="font-medium">Type</span>
                  </div>
                  <div className="text-xs">{interaction.meeting_type}</div>
                </div>
              </div>

              {/* Participants */}
              <div className="space-y-1.5">
                <div className="flex items-center gap-2 text-gray-600">
                  <Users className="w-[14px] h-[14px]" />
                  <span className="font-medium">Participants</span>
                </div>
                {interaction.primary_participants &&
                interaction.primary_participants.length > 0 ? (
                  <div
                    ref={participantsRef}
                    className={`flex flex-wrap gap-2 ${participantVisibleCount === null && !showAllParticipants ? "max-h-[60px] overflow-hidden" : ""}`}
                  >
                    {(showAllParticipants
                      ? interaction.primary_participants
                      : participantVisibleCount !== null
                        ? interaction.primary_participants.slice(
                            0,
                            participantVisibleCount
                          )
                        : interaction.primary_participants
                    ).map((participant: any, index: number) => (
                      <button
                        key={index}
                        onClick={(e) => {
                          if (e.ctrlKey || e.metaKey) {
                            window.open(getEntityUrl(participant), "_blank");
                          } else {
                            openEntityPreview(participant);
                          }
                        }}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800 hover:bg-blue-200 transition-colors cursor-pointer"
                      >
                        <EntityIcon kind={participant.kind} size={12} />
                        {participant.name}
                      </button>
                    ))}
                    {!showAllParticipants &&
                      participantVisibleCount !== null &&
                      participantVisibleCount <
                        interaction.primary_participants.length && (
                        <button
                          onClick={() => setShowAllParticipants(true)}
                          className="inline-flex items-center px-2 py-1 text-xs bg-gray-200 text-blue-600 rounded-md hover:bg-gray-300 transition-colors cursor-pointer"
                        >
                          +
                          {interaction.primary_participants.length -
                            participantVisibleCount}{" "}
                          more...
                        </button>
                      )}
                    {showAllParticipants && (
                      <button
                        onClick={() => {
                          setShowAllParticipants(false);
                          setParticipantVisibleCount(null);
                        }}
                        className="inline-flex items-center px-2 py-1 text-xs bg-gray-200 text-blue-600 rounded-md hover:bg-gray-300 transition-colors cursor-pointer"
                      >
                        Show less
                      </button>
                    )}
                  </div>
                ) : (
                  <span className="text-gray-500 text-xs italic">
                    No participants...
                  </span>
                )}
              </div>

              {/* Mentioned Entities */}
              <div className="space-y-1.5">
                <div className="flex items-center gap-2 text-gray-600">
                  <Link className="w-[14px] h-[14px]" />
                  <span className="font-medium">Mentions</span>
                </div>
                {interaction.mentioned_entities &&
                interaction.mentioned_entities.length > 0 ? (
                  <div
                    ref={mentionsRef}
                    className={`flex flex-wrap gap-2 ${mentionVisibleCount === null && !showAllMentions ? "max-h-[28px] overflow-hidden" : ""}`}
                  >
                    {(showAllMentions
                      ? interaction.mentioned_entities
                      : mentionVisibleCount !== null
                        ? interaction.mentioned_entities.slice(
                            0,
                            mentionVisibleCount
                          )
                        : interaction.mentioned_entities
                    ).map((entity: any, index: number) => (
                      <button
                        key={index}
                        onClick={(e) => {
                          if (e.ctrlKey || e.metaKey) {
                            window.open(getEntityUrl(entity), "_blank");
                          } else {
                            openEntityPreview(entity);
                          }
                        }}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800 hover:bg-blue-200 transition-colors cursor-pointer"
                      >
                        <EntityIcon kind={entity.kind} size={12} />
                        {entity.name}
                      </button>
                    ))}
                    {!showAllMentions &&
                      mentionVisibleCount !== null &&
                      mentionVisibleCount <
                        interaction.mentioned_entities.length && (
                        <button
                          onClick={() => setShowAllMentions(true)}
                          className="inline-flex items-center px-2 py-1 text-xs bg-gray-200 text-blue-600 rounded-md hover:bg-gray-300 transition-colors cursor-pointer"
                        >
                          +
                          {interaction.mentioned_entities.length -
                            mentionVisibleCount}{" "}
                          more...
                        </button>
                      )}
                    {showAllMentions && (
                      <button
                        onClick={() => {
                          setShowAllMentions(false);
                          setMentionVisibleCount(null);
                        }}
                        className="inline-flex items-center px-2 py-1 text-xs bg-gray-200 text-blue-600 rounded-md hover:bg-gray-300 transition-colors cursor-pointer"
                      >
                        Show less
                      </button>
                    )}
                  </div>
                ) : (
                  <span className="text-gray-500 text-xs italic">
                    No mentions...
                  </span>
                )}
              </div>

              {/* Attachments */}
              <div className="space-y-1.5">
                <div className="flex items-center gap-2 text-gray-600">
                  <Paperclip className="w-[14px] h-[14px]" />
                  <span className="font-medium">Attachments</span>
                </div>
                <div className="space-y-1">
                  {localDocumentIds.map((docId: string) => {
                    const filename =
                      interaction.document_filenames?.[docId] || docId;
                    return (
                      <div
                        key={docId}
                        className="flex items-center justify-between px-3 py-2 border border-gray-200 rounded-md hover:bg-gray-100 transition-colors w-full"
                      >
                        <button
                          onClick={() => {
                            openFile({
                              fileId: docId,
                              fileName: filename,
                              fileType: filename
                                .split(".")
                                .pop()
                                ?.toLowerCase(),
                            });
                          }}
                          className="flex items-center gap-2 min-w-0 flex-1 text-left"
                        >
                          <FileText className="w-4 h-4 text-gray-500 flex-shrink-0" />
                          <Tooltip title={filename} arrow placement="top">
                            <span className="text-xs text-gray-700 truncate">
                              {filename}
                            </span>
                          </Tooltip>
                        </button>
                        <button
                          aria-label={`Remove ${filename}`}
                          onClick={() =>
                            handleDocumentIdsChange(
                              localDocumentIds.filter((id) => id !== docId)
                            )
                          }
                          className="text-gray-400 hover:text-red-500 p-1 flex-shrink-0"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    );
                  })}
                  <button
                    onClick={() => setShowDocumentSelectionModal(true)}
                    className="w-full px-3 py-2 border-2 border-dashed border-gray-300 rounded-md text-xs text-gray-600 hover:border-gray-400 hover:text-gray-700 flex items-center justify-center gap-1.5 transition-colors"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    <span>Attach</span>
                  </button>
                </div>
              </div>
            </>
          ) : null}
          <div className="flex items-center justify-end gap-3">
            <NotesContentToggle />
            {hasTranscript && (
              <button
                onClick={() => setShowTranscriptModal(true)}
                className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
              >
                View transcript
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Main content area */}
      <div className="w-full p-4">
        {notesContentMode === "all" && hasEnhancedNotes ? (
          // All view - show both notes and enhanced notes stacked
          <div className="space-y-4">
            {/* Manual notes section. On a group note it carries its own
                banner, the same full-bleed shape as the AI divider below:
                the stacked view already names its AI half, so the shared
                document above it gets its own name rather than reading as
                the toggle's "My Notes" (#11763). The banner lives inside the
                section so a note with no manual content shows neither: a
                bar naming a half that is not on screen would be noise. */}
            {content && !isContentEmpty(content) && (
              <div>
                {manualSectionBannerLabel && (
                  <div
                    data-testid="viewer-manual-section-banner"
                    className="px-4 py-2 mb-4 bg-gray-100 border-y border-gray-200 flex items-center gap-2 -mx-4"
                  >
                    <Users className="w-3.5 h-3.5 text-gray-500" />
                    <span className="text-sm font-medium text-gray-700">
                      {manualSectionBannerLabel}
                    </span>
                  </div>
                )}
                <div
                  dangerouslySetInnerHTML={{ __html: content }}
                  id="fp-note-content"
                  className="max-w-none [&>ul]:list-disc [&>ul]:pl-6 [&>ol]:list-decimal [&>ol]:pl-6 [&>li]:mb-1 [&>p]:mb-4 [&>h1]:text-xl [&>h1]:font-semibold [&>h1]:mb-4 [&>h2]:text-lg [&>h2]:font-semibold [&>h2]:mb-3 [&>h3]:text-base [&>h3]:font-semibold [&>h3]:mb-2"
                  onClick={(e) => {
                    const target = e.target as HTMLElement;
                    const link = target.closest("a");
                    if (link && link.href) {
                      e.preventDefault();
                      e.stopPropagation();
                      window.open(link.href, "_blank", "noopener,noreferrer");
                      return;
                    }
                    removeNodeHighlights("#fp-note-content");
                  }}
                />
              </div>
            )}

            {/* AI Notes Divider Banner */}
            <div className="px-4 py-2 bg-blue-50 border-y border-blue-200 flex items-center gap-2 -mx-4">
              <Sparkles className="w-3.5 h-3.5 text-blue-500" />
              <span className="text-sm font-medium text-blue-700">
                AI Notes
              </span>
            </div>

            {/* AI Notes Section */}
            {responseData?.enhanced_notes && (
              <div>
                <div
                  dangerouslySetInnerHTML={{
                    __html: responseData.enhanced_notes,
                  }}
                  id="fp-note-enhanced-content"
                  className="max-w-none [&>ul]:list-disc [&>ul]:pl-6 [&>ol]:list-decimal [&>ol]:pl-6 [&>li]:mb-1 [&>p]:mb-4 [&>h1]:text-xl [&>h1]:font-semibold [&>h1]:mb-4 [&>h2]:text-lg [&>h2]:font-semibold [&>h2]:mb-3 [&>h3]:text-base [&>h3]:font-semibold [&>h3]:mb-2"
                  onClick={(e) => {
                    const target = e.target as HTMLElement;
                    const link = target.closest("a");
                    if (link && link.href) {
                      e.preventDefault();
                      e.stopPropagation();
                      window.open(link.href, "_blank", "noopener,noreferrer");
                      return;
                    }
                    removeNodeHighlights("#fp-note-enhanced-content");
                  }}
                />
              </div>
            )}
          </div>
        ) : responseData && getDisplayContent() ? (
          <div
            dangerouslySetInnerHTML={{ __html: getDisplayContent() }}
            id="fp-note-content"
            className="max-w-none [&>ul]:list-disc [&>ul]:pl-6 [&>ol]:list-decimal [&>ol]:pl-6 [&>li]:mb-1 [&>p]:mb-4 [&>h1]:text-xl [&>h1]:font-semibold [&>h1]:mb-4 [&>h2]:text-lg [&>h2]:font-semibold [&>h2]:mb-3 [&>h3]:text-base [&>h3]:font-semibold [&>h3]:mb-2"
            onClick={(e) => {
              // Check if the clicked element is a link or contained within a link
              const target = e.target as HTMLElement;
              const link = target.closest("a");

              if (link && link.href) {
                e.preventDefault();
                e.stopPropagation();
                window.open(link.href, "_blank", "noopener,noreferrer");
                return;
              }

              removeNodeHighlights("#fp-note-content");
            }}
          />
        ) : responseData ? (
          <div className="text-gray-500 text-center py-8">
            No contents yet...
          </div>
        ) : // On a failed interaction fetch the metadata band above already
        // names the failure; a perpetual "Loading..." under it would claim
        // the content is still coming.
        interactionError ? null : (
          <div className="text-gray-500 text-center py-8">Loading...</div>
        )}
      </div>

      {/* The interaction page's transcript, read-only: the viewer holds no
          edit permission, and the full page is one click away. */}
      {showTranscriptModal && transcriptContent && (
        <TranscriptModal
          interactionId={interaction?.id ?? ""}
          transcript={transcriptContent}
          canEdit={false}
          onClose={() => setShowTranscriptModal(false)}
        />
      )}

      {showDocumentSelectionModal && (
        <V2FileSelectionModal
          isOpen={showDocumentSelectionModal}
          onClose={() => setShowDocumentSelectionModal(false)}
          onConfirm={(fileIds) => {
            handleDocumentsSelected(fileIds);
            setShowDocumentSelectionModal(false);
          }}
          title="Attach Documents"
          confirmButtonText="Attach"
          excludeBlobKinds={[
            "finpilot-interaction",
            "email",
            "unsupported",
            "unreadable",
          ]}
        />
      )}
    </div>
  );
};

export default FPNotePageViewer;
